// Import Dependencies
import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { withRouter, Redirect } from 'react-router-dom';

// Import Images
import tollfree from '~/common/images/toll-free-icon.svg';

// Import Components
import NewBatch from '~/common/components/NewBatch';
import BackButton from '~/common/components/BackButton';
import SelectNumberButton from '~/common/components/SelectNumberButton';
import CustomTollFreeSearchForm from '~/views/CustomTollFree/components/CustomTollFreeSearchForm';
import SelectedNumberButton from '~/common/components/SelectedNumberButton';
import Centering from '~/common/components/Centering';
import NumberSelection from '~/common/components/NumberSelection';
import Stepper from '~/common/components/Stepper';
import LoadingSpinner from '~/common/components/LoadingSpinner';
import RandomTollFreeLink from '~/common/components/RandomTollFreeLink';
import HeaderGroup from '~/common/components/HeaderGroup';

import { scBeacon, createPersonalizedPurchaseEvent, gtm, viewContentEvent } from '~/common/utils/tracking.js';
import {
    FLOW_STEP_NUMBERS,
    PATH_TRIAL_SIGNUP
} from '~/common/utils/consts.js';

// Import Stylesheet
import styles from './custom-toll-free.css';

@withRouter
@inject('customTollFreeNumbersStore', 'lockNumberStore', 'modalStore', 'progressStore', 'trialSignupStore')
@observer
export default class CustomTollFree extends Component {
    componentDidMount() {
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
        const { progressStore, customTollFreeNumbersStore, trialSignupStore } = this.props;
        const { preselectedCustomTollFreeIntent } = customTollFreeNumbersStore;

        // checks if this is the app entry point and sets corresponding flag
        if (!progressStore.firstView) progressStore.setFirstView(FLOW_STEP_NUMBERS);

        const { isTrialFlow } = trialSignupStore;
        gtm(viewContentEvent(isTrialFlow ? 'trial flow' : 'buy flow', 'numbers', 'toll free'));

        // remove flag of auto redirect once the user has seen this page once
        if (preselectedCustomTollFreeIntent) customTollFreeNumbersStore.setPreselectedCustomTollFreeIntent(false);
    }

    refreshCustomTollFreeNumbers(e) {
        e.preventDefault();
        this.props.customTollFreeNumbersStore.getCustomTollFreeNumbers();
    }

    lockSelectedNumber(e, number, term) {
        e.preventDefault();

        const { lockNumberStore, customTollFreeNumbersStore, trialSignupStore, modalStore } = this.props;

        // set selected number in store
        lockNumberStore.setSelectedNumber(number);
        // set selected number type to toll free
        lockNumberStore.setSelectedNumberType('Custom Toll Free');
        // set selected search term (if exists)
        if (term) customTollFreeNumbersStore.setSelectedCustomTollFreeSearchTerm(term);

        if (trialSignupStore.isTrialFlow && lockNumberStore.isSelectedAreaCodePremium) {
            modalStore.setModalOptions({
                childComponentString: 'upgradeModal',
                isModalClosable: true,
                modalType: 'xlarge',
                isModalOpen: true
            });
        } else {
            lockNumberStore.lockNumberToSession();

            // track SCBeacon: selected custom toll-free number
            scBeacon(createPersonalizedPurchaseEvent('1'));
        }
    }

    render() {
        const { customTollFreeNumbersStore, lockNumberStore, progressStore, trialSignupStore } = this.props;
        // flag denoting search term user searched against, numbers have been fetched, list of numbers, number fetching error
        const { activeSearchTerm, fetchedCustomTollFreeNumbers, fetchingCustomTollFreeNumbers, customTollFreeNumbers, customTollFreeNumbersError, recommendedVanityNumber, vanityTermToNumbers } = customTollFreeNumbersStore;
        // flag denoting if number has been successfully locked, selected number
        const { numberIsLocked, lockingNumber, selectedNumberE164 } = lockNumberStore;

        // if number is successfully locked
        if (numberIsLocked && lockingNumber) {
            // redirect to the trial page if in trial mode
            const redirectView = trialSignupStore.isTrialFlow ? PATH_TRIAL_SIGNUP : progressStore.nextStepFromNumbers;
            // redirect to billing page or plans page if plan is not selected
            return <Redirect push={true} to={redirectView} />;
        }

        // if there is an error fetching numbers
        if (customTollFreeNumbersError) {
            return (
                <div className={styles['custom-toll-free']}>
                    <Centering relative={true}>
                        <Stepper currentStep={FLOW_STEP_NUMBERS} modifiers={styles.stepper} />
                        <BackButton currentStep={FLOW_STEP_NUMBERS} />
                        <HeaderGroup>
                            <h1>
                                Get the perfect <br /> toll free vanity number.
                            </h1>
                        </HeaderGroup>
                    </Centering>
                </div>
            );
        }

        if (fetchingCustomTollFreeNumbers) {
            return (
                <div className={styles['custom-toll-free']}>
                    <Centering relative={true}>
                        <Stepper currentStep={FLOW_STEP_NUMBERS} modifiers={styles.stepper} />
                        <BackButton currentStep={FLOW_STEP_NUMBERS} />
                        <HeaderGroup>
                            <h1>
                                Get the perfect <br /> toll free vanity number.
                            </h1>
                        </HeaderGroup>
                        <CustomTollFreeSearchForm />
                        <div>
                            <p className={styles.recommend}>
                                Loading...
                            </p>
                        </div>
                    </Centering>
                    <NumberSelection>
                        <div className={styles['loading-box']}>
                            <p className={styles['closest-matches']}>
                                Or, select from these closest available matches:
                            </p>
                            <div className={styles.spinner}>
                                <LoadingSpinner />
                            </div>
                        </div>
                    </NumberSelection>
                </div>
            );
        }

        if (!fetchedCustomTollFreeNumbers) {
            return (
                <div className={styles['custom-toll-free']}>
                    <Centering relative={true}>
                        <Stepper currentStep={FLOW_STEP_NUMBERS} modifiers={styles.stepper} />
                        <BackButton currentStep={FLOW_STEP_NUMBERS} />
                        <HeaderGroup>
                            <h1>
                                Get the perfect <br /> toll free vanity number.
                            </h1>
                        </HeaderGroup>
                        <img
                            alt="toll free badge"
                            className={styles.icon}
                            src={tollfree}
                        />
                        <CustomTollFreeSearchForm />
                        <RandomTollFreeLink />
                    </Centering>
                </div>
            );
        }

        return (
            <div className={styles['custom-toll-free']}>
                <Centering relative={true}>
                    <Stepper currentStep={FLOW_STEP_NUMBERS} modifiers={styles.stepper} />
                    <BackButton currentStep={FLOW_STEP_NUMBERS} />
                    <HeaderGroup>
                        <h1>
                            Get the perfect <br /> toll free vanity number.
                        </h1>
                    </HeaderGroup>
                    {!fetchedCustomTollFreeNumbers &&
                        <img
                            alt="toll free badge"
                            className={styles.icon}
                            src={tollfree}
                        />
                    }
                    <CustomTollFreeSearchForm />
                    <div>
                        {activeSearchTerm ?
                            <div>
                                {recommendedVanityNumber ?
                                    <div>
                                        <p className={styles.recommend}>
                                            We recommend...
                                        </p>
                                        {(trialSignupStore.isTrialFlow && customTollFreeNumbersStore.trialValidNumber) ?
                                            <SelectedNumberButton
                                                number={customTollFreeNumbersStore.trialValidNumber}
                                                onClick={(e) => { this.lockSelectedNumber(e, customTollFreeNumbersStore.trialValidNumber, activeSearchTerm); }}
                                                term={activeSearchTerm}
                                                vanity={!!activeSearchTerm}
                                            />
                                            :
                                            <SelectedNumberButton
                                                number={recommendedVanityNumber}
                                                onClick={(e) => { this.lockSelectedNumber(e, recommendedVanityNumber, activeSearchTerm); }}
                                                term={activeSearchTerm}
                                                vanity={!!activeSearchTerm}
                                            />
                                        }
                                    </div>
                                    :
                                    <div>
                                        <p className={styles['no-exact-match']}>
                                            Unfortunately all toll free numbers containing &ldquo;{activeSearchTerm.toUpperCase()}&rdquo;
                                            ({vanityTermToNumbers}) are already taken. Try updating or shortening your search.
                                        </p>
                                        <RandomTollFreeLink />
                                    </div>
                                }
                            </div>
                            :
                            <div>
                                <p className={styles.recommend}>
                                    We recommend...
                                </p>
                                {(trialSignupStore.isTrialFlow && customTollFreeNumbersStore.trialValidNumber) ?
                                    <SelectedNumberButton
                                        number={customTollFreeNumbersStore.trialValidNumber}
                                        onClick={(e) => { this.lockSelectedNumber(e, customTollFreeNumbersStore.trialValidNumber); }}
                                    />
                                    :
                                    <SelectedNumberButton
                                        number={customTollFreeNumbers[0]}
                                        onClick={(e) => { this.lockSelectedNumber(e, customTollFreeNumbers[0]); }}
                                    />
                                }
                            </div>
                        }
                    </div>
                </Centering>
                {!!customTollFreeNumbers.length &&
                    <NumberSelection>
                        <p className={styles['closest-matches']}>
                            Or, select from these closest available matches:
                        </p>
                        <div className={styles['number-selection']}>
                            {customTollFreeNumbers.map((number) => {
                                return (
                                    <SelectNumberButton
                                        key={number.E164}
                                        number={number}
                                        onClick={(e) => { this.lockSelectedNumber(e, number, activeSearchTerm); }}
                                        selected={number.E164 === selectedNumberE164}
                                        term={activeSearchTerm}
                                        vanity={!!activeSearchTerm}
                                    />
                                );
                            })}
                        </div>
                        <div className={styles['get-new-batch']}>
                            <NewBatch onClick={e => this.refreshCustomTollFreeNumbers(e)} />
                            <p className={styles['get-new-batch-text']}>
                                800, 833, 844, 855, 866, 877, and 888 numbers are ALL toll free!
                            </p>
                        </div>
                    </NumberSelection>}
            </div>
        );
    }
}
